import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";
import { OBJECT_TYPES_SCHEDULE_ITEM } from "../../constants/fast";
import {
  getCurrentTimelineQuery,
  invalidateTimelineRelatedQueries,
} from "../../utils/fast";
import useFASTScheduleWebSocket from "../useFASTScheduleWebSocket";
import useTimebounds from "../useTimebounds";

import type { UseMutationResult } from "@tanstack/react-query";

import type { Query } from "../../api/BaseAPI";
import type {
  AddScheduleListMediaResponse,
  GetScheduleAllScheduleListResponse,
  ScheduleItemWithScheduleId,
} from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";
import type { MediaLibraryDragItem } from "../../types";

export interface MutationParams {
  mediaToAdd: MediaLibraryDragItem;
  startTime: number;
  scheduleId: string;
  interstitialAdbreakId?: string | null;
}

export interface Context {
  previousScheduleList?: GetScheduleAllScheduleListResponse;
  newScheduleItem: ScheduleItemWithScheduleId;
}

function useAddScheduleListMediaMutation(): UseMutationResult<
  AddScheduleListMediaResponse,
  unknown,
  MutationParams,
  Context | null
> {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");
  const queryClient = useQueryClient();

  const { currentDay } = useTimebounds();
  const { confirmUpdatedSchedule } = useFASTScheduleWebSocket();

  const optimisticUpdate = ({
    mediaToAdd,
    startTime,
    queryKey,
    newId,
    scheduleId,
    interstitialAdbreakId,
  }: {
    mediaToAdd: MediaLibraryDragItem;
    startTime: number;
    queryKey: Query<GetScheduleAllScheduleListResponse>["queryKey"];
    newId?: string;
    scheduleId: string;
    interstitialAdbreakId?: string | null;
  }): {
    previousScheduleList?: GetScheduleAllScheduleListResponse;
    newScheduleItem: ScheduleItemWithScheduleId;
  } => {
    const previousScheduleList: GetScheduleAllScheduleListResponse | undefined =
      queryClient.getQueryData(queryKey);
    const newScheduleItem: ScheduleItemWithScheduleId = {
      uuid: newId ?? mediaToAdd.uuid,
      user_uuid: "",
      object_uuid: mediaToAdd.mediaId,
      object_label: mediaToAdd.label,
      start_time: moment(startTime).toISOString(),
      end_time: moment(startTime)
        .add(mediaToAdd.duration, "milliseconds")
        .toISOString(),
      object_type: OBJECT_TYPES_SCHEDULE_ITEM.OTT_HOSTED,
      object_duration: mediaToAdd.duration,
      object_total_duration: mediaToAdd.duration,
      object_format: mediaToAdd.formatKey ?? null,
      fast_channel_schedule_uuid: scheduleId,
      interstitial_adbreak_config: {
        adbreaks_at: null,
        cuepoints:
          interstitialAdbreakId && mediaToAdd.cuepoints
            ? mediaToAdd.cuepoints
            : null,
      },
      interstitial_adbreak_uuid: interstitialAdbreakId ?? null,
      updated_at: moment().toISOString(),
      object_picture: mediaToAdd.poster ?? null,
    };

    if (previousScheduleList) {
      queryClient.setQueryData(queryKey, {
        ...previousScheduleList,
        data: [...previousScheduleList.data, newScheduleItem],
      });
    }

    return { previousScheduleList, newScheduleItem };
  };

  const addScheduleListMediaMutation = useMutation({
    mutationKey: ["addScheduleListMedia", channelId],
    mutationFn: async ({
      mediaToAdd,
      startTime,
      scheduleId,
      interstitialAdbreakId,
    }: MutationParams): Promise<AddScheduleListMediaResponse> => {
      const media = {
        object_uuid: mediaToAdd.mediaId,
        start_time: moment(startTime).toISOString(),
        interstitial_adbreak_uuid: interstitialAdbreakId,
      };

      return FastAPI.addScheduleListMedia({
        channelId,
        scheduleId,
        media,
      });
    },
    onMutate: async ({
      mediaToAdd,
      startTime,
      scheduleId,
      interstitialAdbreakId,
    }): Promise<Context | null> => {
      const { currentQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });
      await queryClient.cancelQueries(currentQuery);

      const { previousScheduleList, newScheduleItem } = optimisticUpdate({
        mediaToAdd,
        startTime,
        queryKey: currentQuery,
        scheduleId,
        interstitialAdbreakId,
      });
      return { previousScheduleList, newScheduleItem };
    },
    onSuccess: (
      { data },
      { scheduleId, mediaToAdd, startTime },
      context,
    ): void => {
      if (!context?.previousScheduleList) return;

      const { currentQuery, nextQuery, previousQuery } =
        getCurrentTimelineQuery({
          currentDay,
          channelId,
        });

      queryClient.setQueryData(currentQuery, {
        ...context.previousScheduleList,
        data: [
          ...context.previousScheduleList.data,
          {
            ...context.newScheduleItem,
            uuid: data.uuid,
            user_uuid: data.user_uuid,
            interstitial_adbreak_config: {
              adbreaks_at: data.interstitial_adbreak_config.adbreaks_at,
              cuepoints: data.interstitial_adbreak_config.cuepoints,
            },
          },
        ],
      });

      confirmUpdatedSchedule(currentDay);

      optimisticUpdate({
        mediaToAdd,
        startTime,
        queryKey: previousQuery,
        newId: data.uuid,
        scheduleId,
      });

      optimisticUpdate({
        mediaToAdd,
        startTime,
        queryKey: nextQuery,
        newId: data.uuid,
        scheduleId,
      });

      invalidateTimelineRelatedQueries({
        currentDay,
        channelId,
        queryClient,
        scheduleId,
      });

      queryClient.invalidateQueries({
        queryKey: ["getScheduleVariantDetails"],
      });

      toast.success(t("programAddSuccess"));
    },
    onError: (error, _, context): void => {
      const { currentQuery } = getCurrentTimelineQuery({
        currentDay,
        channelId,
      });

      queryClient.setQueryData(currentQuery, context?.previousScheduleList);
      console.error(error);

      toast.error(t("programAddError"));
    },
  });

  return addScheduleListMediaMutation;
}

export default useAddScheduleListMediaMutation;
